import type { NetworkStat } from "./types";

/**
 * Tallies network activity by observing the browser's own `PerformanceResourceTiming` buffer rather
 * than wrapping `fetch`. A `PerformanceObserver` is passive and batched by the UA, so this adds no
 * cost to the request path itself. Buckets fetch/XHR calls into `api` / `appsync` / `graphql` / `tus`
 * (by host) and lumps static assets under their `initiatorType`.
 */
class NetworkMetricsServiceImpl {
  private observer: PerformanceObserver | null = null;
  private readonly stats = new Map<string, NetworkStat>();
  private apiHosts: string[] = [];

  /** Begins observing. `apiHosts` are origins/URLs that should bucket as first-party `api` calls. */
  public start(apiHosts: readonly string[] = []): void {
    if (this.observer) return;
    if (typeof PerformanceObserver === "undefined") return;

    this.apiHosts = apiHosts.map((h) => this.safeHost(h)).filter((h): h is string => !!h);

    try {
      this.observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === "resource") this.record(entry as PerformanceResourceTiming);
        }
      });
      // `buffered: true` replays entries that landed before we started observing (e.g. early boot calls).
      this.observer.observe({ type: "resource", buffered: true });
    } catch {
      this.observer = null;
    }
  }

  public stop(): void {
    this.observer?.disconnect();
    this.observer = null;
  }

  private record(entry: PerformanceResourceTiming): void {
    const group = this.classify(entry.name, entry.initiatorType);
    const existing = this.stats.get(group);
    const stat: NetworkStat = existing ?? {
      group,
      count: 0,
      totalDurationMs: 0,
      maxDurationMs: 0,
      transferBytes: 0,
    };

    stat.count += 1;
    stat.totalDurationMs += entry.duration;
    if (entry.duration > stat.maxDurationMs) stat.maxDurationMs = entry.duration;
    stat.transferBytes += entry.transferSize ?? 0;

    if (!existing) this.stats.set(group, stat);
  }

  private classify(url: string, initiatorType: string): string {
    const host = this.safeHost(url);
    if (host) {
      if (host.includes("appsync-api")) return "appsync";
      if (host.includes("tusd")) return "tus";
      if (this.apiHosts.some((apiHost) => host === apiHost || host.endsWith(`.${apiHost}`))) return "api";
    }
    if (url.includes("/graphql")) return "graphql";
    // fetch/xmlhttprequest to other hosts vs. static assets (script/css/img/...)
    if (initiatorType === "fetch" || initiatorType === "xmlhttprequest") return "other-xhr";
    return initiatorType || "other";
  }

  private safeHost(value: string): string | null {
    try {
      return new URL(value).host;
    } catch {
      return null;
    }
  }

  public getStats(): NetworkStat[] {
    return [...this.stats.values()].sort((a, b) => b.count - a.count);
  }

  public getTotals(): { count: number; totalDurationMs: number; maxDurationMs: number; transferBytes: number } {
    let count = 0;
    let totalDurationMs = 0;
    let maxDurationMs = 0;
    let transferBytes = 0;
    for (const stat of this.stats.values()) {
      count += stat.count;
      totalDurationMs += stat.totalDurationMs;
      transferBytes += stat.transferBytes;
      if (stat.maxDurationMs > maxDurationMs) maxDurationMs = stat.maxDurationMs;
    }
    return { count, totalDurationMs, maxDurationMs, transferBytes };
  }

  public reset(): void {
    this.stats.clear();
  }
}

export const NetworkMetricsService = new NetworkMetricsServiceImpl();
